import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import type {
  GrantAccessInput,
  GrantAccessResult,
  JourneyState,
  MyTiers,
  PlacementAssessment,
  PlacementQuestion,
  PlacementResponses,
  PlacementResult,
  Roadmap,
  RoadmapItem,
  SkillScore,
  SkillSlug,
  SkillTier,
  StartedAttempt,
  TierLevel,
} from '../api/types';

/**
 * Employee-journey query/mutation hooks — backed by `services/journey/handler.py`.
 *
 * The Lambda speaks snake_case JSON; we map to the camelCase TS types in
 * `api/types.ts` at the hook boundary so components never see wire casing.
 * Every call targets the REST path the backend registers in ROUTES
 * (infrastructure/lib/orchestration-stack.ts), e.g. GET /journey/tiers — NOT
 * the operationId. Paths must match a registered route or the gateway 404s
 * (which the browser surfaces as a CORS error).
 *
 * Hard-gate note: the roadmap's `unlocked` flag is computed server-side
 * (tier <= highest-unlocked OR instructor override); the UI only renders the
 * Lock affordance, never decides access.
 */

export const journeyKeys = {
  all: ['journey'] as const,
  placement: () => [...journeyKeys.all, 'placement'] as const,
  tiers: () => [...journeyKeys.all, 'tiers'] as const,
  roadmap: () => [...journeyKeys.all, 'roadmap'] as const,
};

// --- wire (snake_case) -> domain (camelCase) mappers ----------------------

interface WireQuestion {
  id: string;
  skill_slug: string;
  order_index: number;
  prompt: string;
  points: number;
  options?: ReadonlyArray<{ id: string; text: string }>;
}

interface WireAssessment {
  assessment_id?: number;
  slug: string;
  name: string;
  source: string;
  questions?: ReadonlyArray<WireQuestion>;
}

function mapQuestion(q: WireQuestion): PlacementQuestion {
  return {
    id: q.id,
    skillSlug: q.skill_slug as SkillSlug,
    orderIndex: q.order_index,
    prompt: q.prompt,
    points: q.points,
    options: (q.options ?? []).map((o) => ({ id: o.id, text: o.text })),
  };
}

function mapAssessment(w: WireAssessment): PlacementAssessment {
  const out: PlacementAssessment = {
    slug: w.slug,
    name: w.name,
    source: w.source,
    questions: (w.questions ?? []).map(mapQuestion),
  };
  if (w.assessment_id !== undefined) out.assessmentId = w.assessment_id;
  return out;
}

interface WireTier {
  skill_slug: string;
  current_tier_level: number;
  highest_unlocked_tier_level: number;
  proficiency_score: number | null;
  source: string;
}

function mapTier(t: WireTier): SkillTier {
  return {
    skillSlug: t.skill_slug as SkillSlug,
    currentTierLevel: t.current_tier_level as TierLevel,
    highestUnlockedTierLevel: t.highest_unlocked_tier_level as TierLevel,
    proficiencyScore: t.proficiency_score,
    source: t.source,
  };
}

interface WireRoadmapItem {
  skill_slug: string;
  tier_level: number;
  content_type: 'course' | 'lab' | 'scenario';
  content_slug: string;
  is_required: boolean;
  is_builtin: boolean;
  unlocked: boolean;
}

function mapRoadmapItem(i: WireRoadmapItem): RoadmapItem {
  return {
    skillSlug: i.skill_slug as SkillSlug,
    tierLevel: i.tier_level as TierLevel,
    contentType: i.content_type,
    contentSlug: i.content_slug,
    isRequired: i.is_required,
    isBuiltin: i.is_builtin,
    unlocked: i.unlocked,
  };
}

// --- queries --------------------------------------------------------------

/** GET /journey/placement — the answer-key-stripped placement quiz. */
export function usePlacementAssessment(options?: { enabled?: boolean }) {
  return useQuery<PlacementAssessment>({
    queryKey: journeyKeys.placement(),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<WireAssessment>('/journey/placement', {
        method: 'GET',
        signal,
      });
      return mapAssessment(resp);
    },
  });
}

/** GET /journey/tiers — per-skill tier state + onboarding status. */
export function useMyTiers(options?: { enabled?: boolean }) {
  return useQuery<MyTiers>({
    queryKey: journeyKeys.tiers(),
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<{
        user_id: number;
        journey: { state: JourneyState; placement_attempt_id: number | null };
        tiers?: ReadonlyArray<WireTier>;
      }>('/journey/tiers', { method: 'GET', signal });
      return {
        userId: resp.user_id,
        journey: {
          state: resp.journey.state,
          placementAttemptId: resp.journey.placement_attempt_id,
        },
        tiers: (resp.tiers ?? []).map(mapTier),
      };
    },
  });
}

/** GET /journey/roadmap — tier-ordered recommended content with lock state. */
export function useRoadmap(options?: { enabled?: boolean }) {
  return useQuery<Roadmap>({
    queryKey: journeyKeys.roadmap(),
    enabled: options?.enabled ?? true,
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<{ user_id: number; items?: ReadonlyArray<WireRoadmapItem> }>(
        '/journey/roadmap',
        { method: 'GET', signal },
      );
      return {
        userId: resp.user_id,
        items: (resp.items ?? []).map(mapRoadmapItem),
      };
    },
  });
}

// --- mutations ------------------------------------------------------------

/** POST /journey/placement/start — opens an attempt (returns the quiz + timer). */
export function useStartAttempt() {
  return useMutation<StartedAttempt, Error, void>({
    mutationFn: async () => {
      const resp = await apiFetch<WireAssessment & { started_at: string; status: 'in_progress' }>(
        '/journey/placement/start',
        { method: 'POST', body: {} },
      );
      return {
        ...mapAssessment(resp),
        startedAt: resp.started_at,
        status: 'in_progress',
      };
    },
  });
}

/** POST /journey/placement/submit — grades + places, then refreshes tiers/roadmap. */
export function useSubmitAttempt() {
  const qc = useQueryClient();
  return useMutation<PlacementResult, Error, { responses: PlacementResponses }>({
    mutationFn: async ({ responses }) => {
      const resp = await apiFetch<{
        attempt_id: number;
        status: 'placed';
        placement: Record<string, number>;
        scores: Record<string, { score_pct: number; points_earned: number; points_possible: number }>;
      }>('/journey/placement/submit', { method: 'POST', body: { responses } });

      const placement: Record<string, TierLevel> = {};
      for (const [skill, lvl] of Object.entries(resp.placement)) {
        placement[skill] = lvl as TierLevel;
      }
      const scores: Record<string, SkillScore> = {};
      for (const [skill, s] of Object.entries(resp.scores)) {
        scores[skill] = {
          scorePct: s.score_pct,
          pointsEarned: s.points_earned,
          pointsPossible: s.points_possible,
        };
      }
      return { attemptId: resp.attempt_id, status: 'placed', placement, scores };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: journeyKeys.tiers() });
      qc.invalidateQueries({ queryKey: journeyKeys.roadmap() });
    },
  });
}

/** POST /journey/access/grant — instructor override unlocking one content item. */
export function useGrantAccess() {
  const qc = useQueryClient();
  return useMutation<GrantAccessResult, Error, GrantAccessInput>({
    mutationFn: async (input) => {
      const resp = await apiFetch<{
        override_id: number;
        user_id: number;
        content_type: string;
        content_slug: string;
        status: 'granted';
      }>('/journey/access/grant', {
        method: 'POST',
        body: {
          user_id: input.userId,
          content_type: input.contentType,
          content_slug: input.contentSlug,
          ...(input.reason !== undefined ? { reason: input.reason } : {}),
        },
      });
      return {
        overrideId: resp.override_id,
        userId: resp.user_id,
        contentType: resp.content_type,
        contentSlug: resp.content_slug,
        status: resp.status,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: journeyKeys.roadmap() });
    },
  });
}
